import { animationsConfig } from '@/config/animationsConfig';
import { useChatApi } from '@/hooks/useChatApi';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';
import { useConfigStore } from '@/store/configStore';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useChatStore } from '../store/chatStore';
import { cn } from '../utils/twMerge';
import ContextSuggestion from './ContextSuggestion';
import FloatingButton from './FloatingButton';
import FullscreenImage from './FullscreenImage';
import GDPR from './GDPR';
import useBreakpoints from './hooks/useBreakpoints';

type ChatWrapperProps = {
  children: React.ReactNode;
  cssVariables: Record<string, string>;
};

const ChatWrapper = ({ children, cssVariables }: ChatWrapperProps) => {
  const { fullScreen, isOpen, messageWhileOpen } = useChatStore();
  const { isMobile } = useBreakpoints();
  const { config } = useConfigStore();

  const { sendSuggestion } = useChatApi();

  const chatWrapperRef = useRef<HTMLDivElement>(null);

  const hasContextSuggestions =
    config.contextSuggestions && Object.keys(config.contextSuggestions).length > 0;

  useEffect(() => {
    if (isOpen) {
      if (messageWhileOpen) {
        sendSuggestion(messageWhileOpen);
        useChatStore.getState().setMessageWhileOpen(null);
      }
    }
  }, [isOpen, messageWhileOpen, sendSuggestion]);

  const inactivityConfig = config.ui?.inactivity;
  const inactivityEnabled = inactivityConfig?.enabled ?? true;
  const inactivityTimeoutMs = inactivityConfig?.timeout ?? 10 * 60 * 1000;
  const warningLeadMs = inactivityConfig?.warningLead ?? 60 * 1000;

  useInactivityTimer({
    enabled: inactivityEnabled,
    timeoutMs: inactivityTimeoutMs,
    warningLeadMs,
    isOpen,
  });

  const generalSpecificVariant = config.ui?.generalSpecificVariant;
  const floatingButtonPosition = config.ui?.floatingButton?.position;
  const hasCustomFloatingPosition =
    floatingButtonPosition?.right !== undefined && floatingButtonPosition?.bottom !== undefined;

  const toCssValue = (value?: number | string) => {
    if (value === undefined) {
      return undefined;
    }
    return typeof value === 'number' ? `${value}px` : value;
  };

  const clipPathOrigin = hasCustomFloatingPosition
    ? `calc(100% - (${toCssValue(floatingButtonPosition?.right)} + 5px)) calc(100% - (${toCssValue(
        floatingButtonPosition?.bottom
      )} + 20px))`
    : 'calc(100% - 25px) calc(100% - 45px)';

  return (
    <div className="relative h-full w-full" style={cssVariables as React.CSSProperties}>
      <LayoutGroup>
        <AnimatePresence>
          <motion.div
            className={cn(
              'pointer-events-none fixed bottom-0 right-0 h-[100dvh] w-full items-end px-0 min-[968px]:px-[20px]',
              {
                'px-0 min-[968px]:pb-[20px]': generalSpecificVariant === 'velio' && !fullScreen,
                '!px-0': generalSpecificVariant === 'velio' && fullScreen,
              }
            )}
            style={{
              display: 'flex',
              justifyContent: fullScreen ? 'center' : 'flex-end',
            }}
            transition={{
              ease: 'easeInOut',
              duration: animationsConfig.chatWrapper.fullScreenModeChangeDuration,
            }}
            animate={fullScreen ? 'fullScreen' : 'default'}
          >
            <motion.div
              ref={chatWrapperRef}
              layout
              initial={'closed'}
              variants={{
                closed: {
                  width: isMobile ? '100%' : fullScreen ? '100%' : '35%',
                  height: isMobile ? '100dvh' : fullScreen ? '100dvh' : '80dvh',
                  borderRadius: isMobile ? '0' : '10px 10px 0 0',
                  clipPath: `circle(0px at ${clipPathOrigin})`,
                  maxWidth: generalSpecificVariant === 'velio' ? '100%' : '780px',
                },
                default: {
                  width: '35%',
                  height: '80dvh',
                  borderRadius: generalSpecificVariant === 'velio' ? '10px' : '10px 10px 0 0',
                  clipPath: `circle(150% at ${clipPathOrigin})`,
                },
                fullScreen: {
                  width: '100%',
                  height: '100dvh',
                  borderRadius: '0px',
                  maxWidth: generalSpecificVariant === 'velio' ? '100%' : '780px',
                  clipPath: `circle(150vw)`,
                },
              }}
              animate={
                isOpen
                  ? isMobile
                    ? 'fullScreen'
                    : fullScreen
                      ? 'fullScreen'
                      : 'default'
                  : 'closed'
              }
              transition={{
                ease: 'easeInOut',
                duration: animationsConfig.chatWrapper.fullScreenModeChangeDuration,
                clipPath: {
                  duration: animationsConfig.chatWrapper.circleAnimationDuration,
                },
              }}
              className={cn(
                'pointer-events-auto relative min-w-[320px] border-[1px] border-border bg-white shadow-widget-window'
              )}
            >
              {children}
              <FullscreenImage />
            </motion.div>
            {hasContextSuggestions && <ContextSuggestion />}
            <FloatingButton />
            <GDPR />
          </motion.div>
        </AnimatePresence>
      </LayoutGroup>
    </div>
  );
};

export default ChatWrapper;
